/**
 * Session replay: who gets recorded, and who decides.
 *
 * Replay starts only when ALL of these hold:
 *
 * 1. PostHog is enabled at all (`lib/posthog` — key present, prod build).
 *    Checked in `syncSessionReplay`, NOT in `shouldRecord`.
 * 2. `feature-analytics-session-replay` is on. Default OFF, so replay
 *    stays dark until someone arms it in the dashboard, and one switch
 *    turns it off everywhere without a redeploy.
 * 3. The visitor is authenticated. Anonymous traffic is never recorded —
 *    it is also the traffic we know least about, so consent is meaningless
 *    and a replay is least useful.
 * 4. The visitor has not opted out (Settings -> Privacy).
 * 5. The session won a `REPLAY_SAMPLE_RATE` roll.
 *
 * Recording stops as soon as 3 or 4 stops holding, because both push a
 * re-render into `useSessionReplay`. Gate 2 is different: nothing pushes a
 * flag change, so turning the flag off reaches an open tab only on
 * posthog-js's ~5-minute poll (and not at all while the tab is hidden).
 *
 * ## Posture
 *
 * Recording is ON by default for authenticated users and revocable from
 * Settings — not gated behind an up-front consent prompt. That is a
 * product decision made for an India-only launch; it is NOT the posture
 * GDPR expects, so serving EU users means revisiting rule 4 before that
 * happens, not after. `docs/guides/developer/analytics.md` carries the
 * same warning where a non-engineer will find it.
 *
 * ## Why the stop is not GATED on posthog-js's own state
 *
 * `posthog.sessionRecordingStarted()` reports the LAZY-LOADED recorder
 * (`!!_lazyLoadedSessionRecording?.isStarted`), while
 * `startSessionRecording()` only flips `disable_session_recording` and
 * kicks off a fetch for the recorder script. Between the two, recording
 * is armed but `started` is still false, so a stop gated on it would
 * drop an opt-out or a logout that landed inside that window.
 *
 * `recordingRequested` tracks OUR intent, which is knowable immediately,
 * and the stop fires when EITHER that or the SDK says something might be
 * running — the SDK read only ever widens the set of cases we stop in, it
 * never blocks one. `loadRecorderAndArm` then removes the window itself.
 *
 * ## Masking
 *
 * Configured at init in `lib/posthog`, not here, because rrweb reads it
 * when recording starts and a later change would not apply to a session
 * already underway.
 */

import { FEATURE_FLAGS } from "@shared/types";
import { isEnabled } from "./feature-flags";
import { captureException, getPostHogClient } from "./posthog";

/** Share of eligible authenticated sessions that get recorded. */
export const REPLAY_SAMPLE_RATE = 0.1;

/**
 * Opt-OUT, not opt-in: absent means recorded. Storing the negative is what
 * makes the default survive a cleared browser, which storing `optIn=true`
 * would not.
 */
const OPT_OUT_KEY = "finishd.replay.opt-out";

/**
 * The sample roll, kept for the tab's lifetime. Re-rolling per navigation
 * would record a scattering of fragments from ~10% of page views instead
 * of complete sessions from ~10% of users, which is not the same data and
 * is much less useful.
 */
const SAMPLE_KEY = "finishd.replay.sampled";

// ---------------------------------------------------------------------------
// Storage, which is allowed to be absent, unreadable or unwritable
// ---------------------------------------------------------------------------

/**
 * The store is passed as a THUNK, not as a value, so that reading the
 * `localStorage` global happens inside the `try`. With site data blocked
 * or inside a sandboxed iframe the property access itself throws, and a
 * `readStorage(localStorage, ...)` call would have thrown in the caller's
 * frame before this function was entered — which, for `isReplayOptedOut`,
 * means throwing inside a `useSyncExternalStore` snapshot during render.
 */
type StorageRead =
	| { readable: true; value: string | null }
	| { readable: false };

function readStorage(getStorage: () => Storage, key: string): StorageRead {
	try {
		return { readable: true, value: getStorage().getItem(key) };
	} catch {
		return { readable: false };
	}
}

/** Returns whether the value was actually persisted. */
function writeStorage(
	getStorage: () => Storage,
	key: string,
	value: string,
): boolean {
	try {
		getStorage().setItem(key, value);
		return true;
	} catch {
		return false;
	}
}

const localStore = () => localStorage;
const sessionStore = () => sessionStorage;

// ---------------------------------------------------------------------------
// Opt-out, as a subscribable store
// ---------------------------------------------------------------------------

const listeners = new Set<() => void>();

/**
 * Holds the choice when it could NOT be persisted. A swallowed write would
 * otherwise mean the switch springs back to "on" and recording continues —
 * the only control a user has over this, failing silently in exactly the
 * browsers belonging to people who care. `null` means storage is
 * authoritative.
 */
let optOutFallback: boolean | null = null;

/** Attached lazily, and only once, however many components subscribe. */
let storageListener: ((event: StorageEvent) => void) | null = null;

function notify(): void {
	for (const listener of listeners) listener();
}

/**
 * True when this browser has turned replay off.
 *
 * The in-memory answer wins over storage. It is only ever set when a write
 * FAILED, so it is the more recent decision by construction — and storage
 * holding a stale `"false"` from an earlier opt-in must not shadow an
 * opt-out we could not persist.
 */
export function isReplayOptedOut(): boolean {
	if (optOutFallback !== null) return optOutFallback;

	const read = readStorage(localStore, OPT_OUT_KEY);
	// Unreadable is NOT consent. We cannot know the choice, so we fail
	// toward not recording — the same direction as `recorderMightBeRunning`
	// below. Reading "no value" as "go ahead" would make a locked-down
	// browser the one place the user cannot decline.
	if (!read.readable) return true;

	return read.value === "true";
}

/**
 * Record the choice and act on it immediately — a user who switches replay
 * off should not stay recorded until their next navigation.
 */
export function setReplayOptedOut(optedOut: boolean): void {
	const persisted = writeStorage(localStore, OPT_OUT_KEY, String(optedOut));
	// Only shadow storage when storage did not take it.
	optOutFallback = persisted ? null : optedOut;
	notify();
}

/**
 * `useSyncExternalStore` subscription for the settings toggle.
 *
 * Also listens for `storage`, which fires in OTHER tabs. Without it a user
 * with two tabs open who opts out in one stays recorded in the other
 * indefinitely: nothing else re-reads the key there, because
 * `useSessionReplay` passes its captured `optedOut` into the gate and that
 * shadows storage.
 */
export function subscribeToReplayOptOut(listener: () => void): () => void {
	listeners.add(listener);

	if (!storageListener) {
		storageListener = (event: StorageEvent) => {
			// `key === null` is another tab calling `localStorage.clear()`.
			if (event.key !== OPT_OUT_KEY && event.key !== null) return;
			// Only a KEYED event is a durable write by another tab, and only
			// then does storage become authoritative again. A `clear()`
			// persisted nothing, so a choice we are holding in memory
			// because it could not be written must survive it.
			if (event.key === OPT_OUT_KEY) optOutFallback = null;
			notify();
		};
		window.addEventListener("storage", storageListener);
	}

	return () => {
		listeners.delete(listener);
		if (listeners.size === 0 && storageListener) {
			window.removeEventListener("storage", storageListener);
			storageListener = null;
		}
	};
}

// ---------------------------------------------------------------------------
// Sampling
// ---------------------------------------------------------------------------

/**
 * Remembered in module scope as well as `sessionStorage`. The module copy
 * is what holds the decision steady when `sessionStorage` is blocked —
 * without it every call re-rolls, and replay flickers on and off across
 * flag payloads instead of holding one answer for the tab.
 */
let sampleMemo: boolean | null = null;

/** Whether this session is in the sample. Rolls once and remembers. */
export function isSessionSampled(): boolean {
	if (sampleMemo !== null) return sampleMemo;

	const stored = readStorage(sessionStore, SAMPLE_KEY);
	if (stored.readable && stored.value !== null) {
		sampleMemo = stored.value === "true";
		return sampleMemo;
	}

	sampleMemo = Math.random() < REPLAY_SAMPLE_RATE;
	writeStorage(sessionStore, SAMPLE_KEY, String(sampleMemo));
	return sampleMemo;
}

// ---------------------------------------------------------------------------
// The gate
// ---------------------------------------------------------------------------

/**
 * `optedOut` may be passed by a caller that already holds a reactive copy
 * — `useSessionReplay` does, and passing it is what makes the toggle a
 * real input to this decision rather than ambient state read behind the
 * caller's back. Omitted, it is read from storage.
 */
export interface ReplayGateInput {
	authenticated: boolean;
	optedOut?: boolean;
}

/**
 * Gates 2 through 5 of the five in the docblock above. Gate 1 — "PostHog
 * is enabled at all" — is NOT checked here; it lives in
 * `syncSessionReplay`, which is the only caller that can act on it. So
 * `shouldRecord(...) === true` means "eligible", not "will be recorded".
 */
export function shouldRecord(options: ReplayGateInput): boolean {
	if (!options.authenticated) return false;
	if (options.optedOut ?? isReplayOptedOut()) return false;
	if (!isEnabled(FEATURE_FLAGS.sessionReplay)) return false;
	// Last, deliberately: an ineligible visitor never rolls, so nothing is
	// written to their `sessionStorage`.
	return isSessionSampled();
}

/**
 * Whether WE have asked posthog-js to record. See the docblock above for
 * why this is tracked here rather than read back from the SDK.
 */
let recordingRequested = false;

/**
 * Invalidates an arm that has not finished loading the recorder yet. Every
 * `syncSessionReplay` bumps it, so a stop issued while the recorder chunk
 * is still downloading cancels the pending start outright rather than
 * racing it.
 */
let armToken = 0;

/** Fail toward stopping: if we cannot tell, assume it might be running. */
function recorderMightBeRunning(
	ph: NonNullable<ReturnType<typeof getPostHogClient>>,
): boolean {
	try {
		return ph.sessionRecordingStarted();
	} catch {
		return true;
	}
}

/**
 * Load the recorder ourselves, then arm.
 *
 * This is what closes the load window rather than racing it. Left alone,
 * `startSessionRecording()` flips `disable_session_recording` and posthog-js
 * then fetches `lazy-recorder` from its CDN. For the whole of that fetch:
 *
 * - `stopSessionRecording()` is INERT — it reaches
 *   `this._lazyLoadedSessionRecording?.stop()`, and that recorder does not
 *   exist yet, so the optional chain swallows the call;
 * - nothing cancels the in-flight script;
 * - and when it lands, `_onScriptLoaded()` calls `start()` WITHOUT
 *   re-reading `disable_session_recording`, so recording begins anyway.
 *
 * Awaiting the import here means `__PosthogExtensions__` is already
 * populated when we arm, so posthog's own `_lazyLoadAndStart` takes its
 * synchronous branch rather than fetching the script — so the unguarded
 * `loadExternalDependency` CALLBACK that caused the bug becomes
 * unreachable. (`_onScriptLoaded` itself still runs; it is what the
 * synchronous branch calls. What disappears is the asynchronous route
 * into it, which is the one nothing re-checks the config bit on.)
 *
 * That is the safety property; "recording starts instantly" is NOT, and
 * it would be wrong to claim it. posthog still needs REMOTE config, so on
 * a cold load — or one whose persisted config is older than its one-hour
 * TTL — arming is a no-op at the moment we call it and recording begins
 * when the config lands. That path is safe for a different reason: it
 * resumes through `startIfEnabledOrStop()`, which re-reads
 * `disable_session_recording`, so a stop issued in the meantime is
 * honoured.
 *
 * It is a dynamic import, so the recorder is a separate chunk fetched only
 * for sessions that are actually recorded — the same network cost as the
 * CDN fetch it replaces, and served from our own origin. Harder for an
 * ad-blocker to reach than PostHog's CDN, though the emitted chunk is
 * still named `lazy-recorder-*.js`, which a filter list can match.
 */
async function loadRecorderAndArm(
	ph: NonNullable<ReturnType<typeof getPostHogClient>>,
	token: number,
): Promise<void> {
	try {
		await import("posthog-js/dist/lazy-recorder");
	} catch {
		// Fails CLOSED: nothing is armed, so nothing records.
		//
		// The token check is defensive and currently unreachable, so no
		// test covers it: both arms import the same specifier, so they
		// resolve or reject together. It is here because without it a
		// superseded arm whose import rejected would clear the intent
		// belonging to a NEWER arm, and that arm would then return below
		// and leave an eligible user unrecorded.
		if (token === armToken) recordingRequested = false;
		return;
	}

	// A stop, a logout or an opt-out landed while the chunk downloaded.
	// The token does the work here; `!recordingRequested` is belt-and-
	// braces. Dropping it alone changes no test, dropping the token does.
	// (The only path that clears the intent WITHOUT bumping the token is
	// the import-failure catch above, and that returns immediately.)
	if (token !== armToken || !recordingRequested) return;

	try {
		ph.startSessionRecording();
	} catch {
		// `set_config` applies the config bit BEFORE the extension hooks
		// that can throw, so recording may in fact be armed. Leaving the
		// intent set is what guarantees the next ineligible sync still
		// issues a stop instead of taking the early exit.
	}
}

/**
 * Bring the recorder in line with the gate. Idempotent: the redundant-call
 * guards matter because both SDK calls go through `set_config`, which
 * re-instantiates session persistence and re-runs a fixed list of seven
 * extension start hooks. Not free, and not worth repeating per flag
 * payload.
 */
export function syncSessionReplay(options: ReplayGateInput): void {
	const ph = getPostHogClient();
	if (!ph) return;

	const wanted = shouldRecord(options);

	// An optimisation, not a correctness guard, and deliberately before
	// the token bump: every flag payload sends a redundant "still
	// recording" sync, and without this each one cancels the pending arm
	// and starts another. The end state is the same either way, which is
	// why no test distinguishes it — it just avoids a needless dynamic
	// import and `set_config` every five minutes.
	if (wanted && recordingRequested) return;

	// Any arm still waiting on the recorder chunk is now superseded.
	const token = ++armToken;

	if (wanted) {
		// Set BEFORE the await, not after: a stop arriving mid-download
		// has to find the intent already set, or it would take the early
		// exit below and never issue a stop at all.
		recordingRequested = true;
		void loadRecorderAndArm(ph, token);
		return;
	}

	if (!recordingRequested && !recorderMightBeRunning(ph)) return;

	try {
		ph.stopSessionRecording();
		recordingRequested = false;
	} catch (error) {
		// Fails OPEN — a recording we asked to end may still be running.
		// The intent deliberately stays set so the next sync retries, and
		// this is the one failure here that must not be silent.
		captureException(error, { surface: "session-replay-stop" });
	}
}

/** Test-only: forget everything this module remembers between cases. */
export function _resetForTests(): void {
	recordingRequested = false;
	armToken = 0;
	optOutFallback = null;
	sampleMemo = null;
	if (storageListener) {
		window.removeEventListener("storage", storageListener);
		storageListener = null;
	}
	listeners.clear();
	try {
		sessionStorage.removeItem(SAMPLE_KEY);
	} catch {
		// nothing to clear
	}
}
